import React, { type PropsWithChildren, type ReactElement, useRef } from 'react'
// reason: FIXME at moment we rely too much on custom styling, we need the print media
// eslint-disable-next-line @dhl/dhl-ui/no-restricted-imports
import { Toolbar } from '@mui/material'
import { useIsMobileVariant, Box } from '@dhl-ui/components'
import { type Header, type SideMenu, type BottomMenu, Skip, bottomMenuHeightSave } from './components'
import { usePage } from './context'

export type BasePageProps = PropsWithChildren<{
  /**
   * Placement of our bottom menu, please use `Page.BottomMenu`
   * You can not have both bottom and side Menu
   * In that case the bottom menu wins
   */
  bottomMenu?: ReactElement<typeof BottomMenu>

  /**
   * Placement of our header, please use `Page.Header`
   */
  header?: ReactElement<typeof Header>

  /**
   * Placement of our left sided menu, please use `Page.SideMenu`
   * You can not have both Bottom and Side Menu
   * In that case the bottom menu wins
   */
  sideMenu?: ReactElement<typeof SideMenu>

  /**
   * Possible settings when you have a Side Menu
   */

  // reason: these are our base props and used in Page and PageWithRefresh components! (which eslint doesn't know about)
  // eslint-disable-next-line react/no-unused-prop-types
  sideMenuOptions?: {
    /**
     * When you need more than our default side menu width
     * @example 220
     * @example '12rem'
     *
     * @default '15rem' (240px)
     */
    width?: number | string

    /**
     *
     * @default true
     */
    isInitiallyOpen?: boolean

    /**
     * Over all apps we store the active toggle state. When you want to give your own you can do this
     *
     * @example `irs-page-menu`
     */
    storageKey?: string
  }

  /**
   * By default, we print our DHL logo on top. But you are able to not print our Header
   * at all by activating `noPrint`
   *
   * @default false
   */
  noPrintHeader?: boolean

  /**
   * Knowing which page you rendered
   */
  'data-testid'?: string
}>

const BasePage = ({ children, header, sideMenu, bottomMenu, noPrintHeader, 'data-testid': testid }: BasePageProps) => {
  const { sideMenuWidth, isOpen } = usePage()
  const isMobile = useIsMobileVariant()
  const contentRef = useRef<HTMLDivElement>(null)

  const isOpenMobileSubMenu = !!sideMenu && isMobile && isOpen

  return (
    <Box
      data-testid={testid}
      sx={{
        // to be able to see our content still when having bottom menu since we use the most basic layout
        pb: bottomMenu ? bottomMenuHeightSave : 0,
        minHeight: '100vh',
        display: 'flex',
      }}
    >
      {header}

      <Box sx={{ flex: 1, width: '100%', display: isOpenMobileSubMenu ? 'block' : 'flex', position: 'relative' }}>
        {sideMenu ? (
          <Box sx={{ displayPrint: 'none' }}>
            {!isMobile && (
              <Box sx={{ width: sideMenuWidth, displayPrint: 'none' }}>
                <Skip inputRef={contentRef} label="menu" />
                {sideMenu}
              </Box>
            )}

            {isMobile && isOpen ? sideMenu : null}
          </Box>
        ) : null}

        {isOpenMobileSubMenu ? null : (
          <Box
            sx={{
              flex: 1,
              // we do not show user when we put focus on content section, since next tab will go to something focusable inside
              outline: 0,
            }}
            data-testid="page-content"
            ref={contentRef}
          >
            {header && (
              <Toolbar
                sx={{
                  '@media print': { ...(noPrintHeader ? { display: 'none' } : {}) },
                }}
              />
            )}

            {children}
          </Box>
        )}
      </Box>
      {bottomMenu}
    </Box>
  )
}

export { BasePage }
